import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { cleanParams } from '@/utils/cleanParams';

import { apiDealFetch } from '..';
import { DEAL_QUERY_KEYS } from './queries';
import {
  AdminOffersRes,
  EntityCompanyListRes,
  EntityOfferListRes,
  GetAdminCompaniesParams,
  GetAdminOffersParams,
  GetEntityOffersParams,
  GetMarketplaceOffersParams,
  MarketplaceOffersRes,
} from './types';

export const _getAdminCompaniesInfinite = (params?: Omit<GetAdminCompaniesParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.adminCompanies, 'infinite', ...Object.values(params ?? {})],
    queryFn: ({ pageParam = 1 }) => {
      const safeParams = cleanParams({ ...params, page: pageParam, limit: 20 });
      return apiDealFetch<EntityCompanyListRes>('/admin/companies', { params: safeParams });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    refetchOnMount: true,
    retry: 1,
  });

const toAdminOffersQuery = (params: GetAdminOffersParams = {}) => {
  const {
    companyIds,
    providerIds,
    structures,
    confidenceScores,
    introducerIds,
    sellerIds,
    ...rest
  } = params;
  return cleanParams({
    ...rest,
    companyIds: companyIds?.length ? companyIds.join(',') : undefined,
    providerIds: providerIds?.length ? providerIds.join(',') : undefined,
    structures: structures?.length ? structures.join(',') : undefined,
    confidenceScores: confidenceScores?.length ? confidenceScores.join(',') : undefined,
    introducerIds: introducerIds?.length ? introducerIds.join(',') : undefined,
    sellerIds: sellerIds?.length ? sellerIds.join(',') : undefined,
  });
};

export const _getAdminOffersInfinite = (params?: Omit<GetAdminOffersParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.adminOffers, 'infinite', params ?? {}],
    queryFn: ({ pageParam = 1 }) =>
      apiDealFetch<AdminOffersRes>('/admin/offers', {
        params: toAdminOffersQuery({ ...params, page: pageParam, limit: 20 }),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    refetchOnMount: true,
    retry: 1,
  });

/**
 * The first matching offer and the total, without the rest of the rows: e.g. the counter on
 * "Posted only by Gamma", whose one row also names Gamma's provider id.
 */
export const _getAdminOffersProbe = (params?: Omit<GetAdminOffersParams, 'page' | 'limit'>) =>
  queryOptions({
    queryKey: [DEAL_QUERY_KEYS.adminOffers, 'probe', params ?? {}],
    queryFn: () =>
      apiDealFetch<AdminOffersRes>('/admin/offers', {
        params: toAdminOffersQuery({ ...params, page: 1, limit: 1 }),
      }),
    retry: 1,
  });

const MARKETPLACE_OFFERS_PATH = '/entity/companies/marketplace-offers';

const toMarketplaceOffersQuery = (params: GetMarketplaceOffersParams = {}) => {
  const { companyIds, statuses, structures, ...rest } = params;
  return cleanParams({
    ...rest,
    companyIds: companyIds?.length ? companyIds.join(',') : undefined,
    statuses: statuses?.length ? statuses.join(',') : undefined,
    structures: structures?.length ? structures.join(',') : undefined,
  });
};

/**
 * Every provider's offers as the session entity may see them (BE-1, GP-4679). `entityId` only
 * scopes the cache: the server reads the entity from the session, and "own" rows differ per entity.
 */
export const _getMarketplaceOffersInfinite = (
  entityId: string | null | undefined,
  params?: Omit<GetMarketplaceOffersParams, 'page'>,
) =>
  infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.marketplaceOffers, 'infinite', entityId, params ?? {}],
    queryFn: ({ pageParam = 1 }) =>
      apiDealFetch<MarketplaceOffersRes>(MARKETPLACE_OFFERS_PATH, {
        params: toMarketplaceOffersQuery({ ...params, page: pageParam, limit: 20 }),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    refetchOnMount: true,
    retry: 1,
  });

/** The total only, e.g. the counter on "Posted only by us". */
export const _getMarketplaceOffersProbe = (
  entityId: string | null | undefined,
  params?: Omit<GetMarketplaceOffersParams, 'page' | 'limit'>,
) =>
  queryOptions({
    queryKey: [DEAL_QUERY_KEYS.marketplaceOffers, 'probe', entityId, params ?? {}],
    queryFn: () =>
      apiDealFetch<MarketplaceOffersRes>(MARKETPLACE_OFFERS_PATH, {
        params: toMarketplaceOffersQuery({ ...params, page: 1, limit: 1 }),
      }),
    // Offers are posted, edited and closed on other pages; the counter catches up on return.
    refetchOnMount: true,
    retry: 1,
  });

/** `entityId`, when given, only scopes the cache: the server reads the entity from the session. */
export const _getEntityOffersInfinite = (
  params?: Omit<GetEntityOffersParams, 'page'>,
  entityId?: string | null,
) =>
  infiniteQueryOptions({
    queryKey: [
      DEAL_QUERY_KEYS.entityOffers,
      'infinite',
      ...Object.values(params ?? {}),
      ...(entityId ? [entityId] : []),
    ],
    queryFn: ({ pageParam = 1 }) => {
      const safeParams = cleanParams({ ...params, page: pageParam, limit: 20 });
      return apiDealFetch<EntityOfferListRes>('/entity/companies/offers', { params: safeParams });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    refetchOnMount: true,
    retry: 1,
  });
